import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import get from 'lodash.get';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { SwatchSelectorMinimal } from 'dibs-swatch-selector/exports/SwatchSelectorMinimal';
import { TileContextProvider } from '../helpers/TileContext';
import { Price } from '../Price';
import { ProductTileMeasurements } from './ProductTileMeasurements';
import { QuickViewButton } from '../QuickViewButton';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { Shipping } from '../Badges/Shipping';
import StorefrontOnlyBadge from '../Badges/StorefrontOnly';
import QuickViewItem from '../QuickViewItem';
import OfferedBy from '../Info/OfferedBy';
import InternalSellerName from '../Info/InternalSellerName';
import {
    CREATOR_UNKNOWN,
    DEFAULT_MEASUREMENT_UNIT,
    DEFAULT_SHIPPING_BADGE_TYPE,
    FREE_SHIPPING,
    FREE_SHIPPING_AND_COST,
} from '../constants';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { Link } from 'dibs-elements/exports/Link';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';

import styles from './ItemTileBase.scss';

const CREATOR_ATTRIBUTED_BY = 'BY';
const CREATOR_ATTRIBUTED_BY_AND_DOCUMENTED = 'BY_AND_DOCUMENTED';
const CREATOR_ATTRIBUTION_LIST = [CREATOR_ATTRIBUTED_BY, CREATOR_ATTRIBUTED_BY_AND_DOCUMENTED];
const TILE_TYPE_PROMINENT = 'PROMINENT_TILE';

/*
    This can be renamed to Tile when work in VERTICALS-77 is completed.
    Prop isSbTile also can be removed then.
*/
export function ItemTileBaseComponent({
    item,
    index,
    imageSize,
    currency,
    priceBold,
    showPrice,
    showBrowseSimilar,
    showMeasurements,
    showMSKUSwatch,
    showHoverState,
    showCreatorName,
    showQuickView,
    showQuickViewIcon,
    showInternalSellerName,
    showSellerName,
    showStorefrontOnlyBadge,
    isMobile,
    measurementUnit,
    itemsPerRow,
    onClick,
    onContentLoaded,
    onContentVisible,
    onQuickViewDetailsClick,
    openQuickView,
    useLoFiLazyLoader,
    imageLoadVerticalOffset,
    activeQuickView,
    closeQuickView,
    hideImagePadding,
    srcSetSizes,
    srcSetWidths,
    srcSetQuality,
    favoritesComponent,
    viewer,
    itemSearch,
    tileType,
    children,
    showLightBox,
    disableBorder,
    disableContentMargin,
    showSwipeIndicator,
    //when using FREE_SHIPPING_AND_COST type we need to provide buyerId, fetchShippingCostInfo variables to relay fragment
    shippingBadgeType,
}) {
    const tileRef = useRef(null);
    const [isHovering, setIsHovering] = useState(false);

    const title = get(item, 'title');
    const localizedPdpUrl = get(item, 'localizedPdpUrl');
    const isArt = get(item, 'isArt');
    const creators = get(item, 'creators') || [];
    const itemId = get(item, 'serviceId');
    const similarItemsHref = `/similar/?item=${itemId}`;
    const attribution = creators.find(el => CREATOR_ATTRIBUTION_LIST.includes(el.attribution));
    const attributionCreatorName = get(attribution, 'creator.displayName');
    const creationDate = get(item, 'classification.creationDate') || null;
    const sellerName = get(item, 'seller.sellerProfile.company');
    const showTileFooter = !isMobile && !showLightBox && (showBrowseSimilar || showMeasurements);
    const shouldShowInternalSellerName = showInternalSellerName && !!sellerName;
    const shouldShowSellerName = !showCreatorName && showSellerName && !!sellerName;
    const isQuickViewActive = index === activeQuickView;
    const isProminentTile = tileType === TILE_TYPE_PROMINENT;
    const linkData = item?.linkData;

    const containerClassNames = classnames(styles.container, {
        [styles.activeHover]: showHoverState || isQuickViewActive,
        [styles.isProminentTile]: isProminentTile,
        [styles.disableBorder]: showLightBox || (disableBorder && !isQuickViewActive),
    });
    const footerClassNames = classnames(styles.footer, {
        [styles.activeHover]: showHoverState,
        [styles.isProminentTile]: isProminentTile,
    });
    const contentClassNames = classnames(styles.content, {
        [styles.isProminentTile]: isProminentTile,
        [styles.disableContentMargin]: disableContentMargin,
    });
    const openQuickViewFunc = () => openQuickView(index);

    const showAttributionCreatorName =
        !!attributionCreatorName && attributionCreatorName.toLowerCase() !== CREATOR_UNKNOWN;

    // data-pk attribute below is used by dibs-toho (MG)
    const titleEl = Heading => (
        <SeoLink
            dataTn="item-tile-title-anchor"
            dataPk={itemId}
            onClick={onClick}
            className={classnames(styles.title, styles.tileLink, styles.titleLink)}
            href={localizedPdpUrl}
            linkData={linkData}
        >
            <Heading className={styles.titleHeader}>
                {isArt ? (
                    <>
                        {showAttributionCreatorName && (
                            <div
                                className={classnames(styles.ellipsis, styles.creator)}
                                data-tn="portfolio-row-creator-name"
                            >
                                {attributionCreatorName}
                            </div>
                        )}
                        <div
                            className={classnames(styles.artTitle, {
                                [styles.artTitleNoCreator]: !showAttributionCreatorName,
                            })}
                        >
                            <span className={styles.ellipsis} data-tn="portfolio-row-product-name">
                                {title}
                            </span>
                            {creationDate && `, ${creationDate}`}
                        </div>
                    </>
                ) : (
                    title
                )}
            </Heading>
        </SeoLink>
    );

    const sharedProductProps = {
        hideProductDetails: true,
        hideDivider: true,
        index,
        onClick,
        title,
        pdpUrl: localizedPdpUrl,
        itemsPerRow,
        onContentLoaded,
        useLoFiLazyLoader,
        imageLoadVerticalOffset,
        hideImagePadding,
        srcSetSizes,
        srcSetWidths,
        srcSetQuality,
        showLightBox,
        showSwipeIndicator,
    };

    return (
        <TileContextProvider imageSize={imageSize}>
            <div
                className={containerClassNames}
                ref={tileRef}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            >
                {shouldShowInternalSellerName && (
                    <InternalSellerName isSbTile sellerName={sellerName} />
                )}
                {onContentVisible && (
                    <VisibilityTracker
                        elementRef={tileRef}
                        onVisibilityChange={({ isVisible }) => {
                            if (isVisible) {
                                onContentVisible();
                            }
                        }}
                    />
                )}
                <HeadingLevel>
                    {Heading => (
                        <>
                            <QuickViewItem
                                isSbTile
                                item={item}
                                itemSearch={itemSearch}
                                isMobile={isMobile}
                                isQuickViewActive={isQuickViewActive}
                                onClose={closeQuickView}
                                onViewDetailsClick={onQuickViewDetailsClick}
                                focusOnActive
                            />

                            <div
                                className={contentClassNames}
                                // make tile inactive and blank when QuickViewItem overlay is active.
                                // visibility: hidden elements still take up space, which is desired in
                                // this instance bc QuickViewItem height depends on this div's height
                                style={isQuickViewActive ? { visibility: 'hidden' } : {}}
                            >
                                {/* nest favoritesComponent inside of content div so it is also hidden when QuickViewItem is active */}
                                {favoritesComponent}
                                <div className={styles.productContainer}>
                                    {!!children &&
                                        children({
                                            ...sharedProductProps,
                                            isHovering,
                                            isMobile,
                                            isQuickViewActive,
                                        })}
                                    {showQuickView && showLightBox && (
                                        <div className={styles.quickViewLightBox}>
                                            <QuickViewButton
                                                isSbTile
                                                openQuickView={openQuickViewFunc}
                                                showQuickViewIcon={showQuickViewIcon}
                                                hideDivider
                                            />
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={classnames({
                                        [styles.info]: !showLightBox,
                                        [styles.infoLightBox]: showLightBox,
                                    })}
                                >
                                    {isMobile && showMeasurements && (
                                        <div
                                            data-tn="product-tile-measurements"
                                            className={`${styles.measurementsWrapper} ${styles.ellipsis}`}
                                        >
                                            <ProductTileMeasurements
                                                item={item}
                                                measurementUnit={measurementUnit}
                                                isMobile={isMobile}
                                            />
                                        </div>
                                    )}
                                    <div className={styles.titleWrapper}>
                                        {titleEl(Heading)}
                                        {showQuickView && !showLightBox && (
                                            <QuickViewButton
                                                isSbTile
                                                openQuickView={openQuickViewFunc}
                                                showQuickViewIcon={showQuickViewIcon}
                                                hideDivider
                                            />
                                        )}
                                    </div>
                                    {showPrice ? (
                                        <Price
                                            isSbTile
                                            currency={currency}
                                            item={item}
                                            priceBold={priceBold}
                                        />
                                    ) : (
                                        <div className={styles.pricePlaceHolder} /> // placeholder to avoid flicker
                                    )}
                                    <Shipping
                                        item={item}
                                        viewer={viewer}
                                        currency={currency}
                                        type={shippingBadgeType}
                                    />
                                    {showStorefrontOnlyBadge && <StorefrontOnlyBadge item={item} />}
                                    {showMSKUSwatch && (
                                        <SwatchSelectorMinimal
                                            displayCount={isMobile ? 3 : 4}
                                            item={item}
                                            path={localizedPdpUrl}
                                            onClick={onClick}
                                        />
                                    )}
                                    {shouldShowSellerName && (
                                        <OfferedBy
                                            className={styles.offeredBy}
                                            sellerName={sellerName}
                                        />
                                    )}
                                    {showTileFooter && (
                                        <div className={footerClassNames}>
                                            <div data-tn="product-tile-measurements">
                                                {showMeasurements && (
                                                    <ProductTileMeasurements
                                                        item={item}
                                                        measurementUnit={measurementUnit}
                                                    />
                                                )}
                                            </div>
                                            {showBrowseSimilar && (
                                                <FormattedMessage
                                                    id="dc.searchProductTile.browseSimilar"
                                                    defaultMessage="Browse Similar"
                                                >
                                                    {msg => (
                                                        <Link
                                                            dataTn="browse-similar"
                                                            href={similarItemsHref}
                                                        >
                                                            {msg}
                                                        </Link>
                                                    )}
                                                </FormattedMessage>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </HeadingLevel>
            </div>
        </TileContextProvider>
    );
}

ItemTileBaseComponent.propTypes = {
    item: PropTypes.object,
    viewer: PropTypes.object,
    itemSearch: PropTypes.object,
    index: PropTypes.number,
    imageSize: PropTypes.string,
    currency: PropTypes.string,
    showBrowseSimilar: PropTypes.bool,
    isMobile: PropTypes.bool,
    showPrice: PropTypes.bool,
    showByLine: PropTypes.bool,
    showMeasurements: PropTypes.bool,
    showMSKUSwatch: PropTypes.bool,
    showHoverState: PropTypes.bool,
    showSellerName: PropTypes.bool,
    priceBold: PropTypes.bool,
    measurementUnit: PropTypes.string,
    itemsPerRow: PropTypes.number,
    showQuickView: PropTypes.bool,
    useLoFiLazyLoader: PropTypes.bool,
    imageLoadVerticalOffset: PropTypes.number,
    showQuickViewIcon: PropTypes.bool,
    showInternalSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    showStorefrontOnlyBadge: PropTypes.bool,
    hideImagePadding: PropTypes.bool,
    srcSetSizes: PropTypes.string,
    srcSetWidths: PropTypes.string,
    srcSetQuality: PropTypes.string,
    favoritesComponent: PropTypes.node,
    activeQuickView: PropTypes.number,
    tileType: PropTypes.string,
    onClick: PropTypes.func,
    onContentLoaded: PropTypes.func,
    onContentVisible: PropTypes.func,
    onQuickViewDetailsClick: PropTypes.func,
    openQuickView: PropTypes.func,
    closeQuickView: PropTypes.func,
    children: PropTypes.func.isRequired,
    showLightBox: PropTypes.bool,
    disableBorder: PropTypes.bool,
    disableContentMargin: PropTypes.bool,
    showSwipeIndicator: PropTypes.bool,
    shippingBadgeType: PropTypes.oneOf([FREE_SHIPPING, FREE_SHIPPING_AND_COST]),
};

ItemTileBaseComponent.defaultProps = {
    imageSize: 'imageMedium',
    currency: 'USD',
    disableLinks: false,
    itemsPerRow: 3,
    useLoFiLazyLoader: false,
    priceBold: false,
    measurementUnit: DEFAULT_MEASUREMENT_UNIT,
    showByLine: true,
    showQuickView: true,
    showQuickViewIcon: true,
    showLightBox: false,
    showPrice: true,
    showMeasurements: true,
    showMSKUSwatch: true,
    showHoverState: false,
    showSellerName: false,
    onQuickViewDetailsClick: () => {},
    viewer: null,
    disableBorder: false,
    disableContentMargin: false,
    itemSearch: null,
    showSwipeIndicator: false,
    shippingBadgeType: DEFAULT_SHIPPING_BADGE_TYPE,
};

export const ItemTileBase = createFragmentContainer(ItemTileBaseComponent, {
    viewer: graphql`
        fragment ItemTileBase_viewer on Viewer
        @argumentDefinitions(
            fetchRegionalInfo: { type: "Boolean", defaultValue: false }
            userZipCode: { type: "String", defaultValue: "" }
            userCountryCode: { type: "String", defaultValue: "" }
        ) {
            ...Shipping_viewer
            @arguments(
                fetchRegionalInfo: $fetchRegionalInfo
                userZipCode: $userZipCode
                userCountryCode: $userCountryCode
            )
        }
    `,
    itemSearch: graphql`
        fragment ItemTileBase_itemSearch on ItemSearchQueryConnection {
            ...QuickViewItem_itemSearch
        }
    `,
    item: graphql`
        fragment ItemTileBase_item on Item
        @argumentDefinitions(
            isTrade: { type: "Boolean", defaultValue: false }
            showSeller: { type: "Boolean", defaultValue: false }
            pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
            showCreatorLinksQuicklook: { type: "Boolean", defaultValue: false }
            buyerId: { type: "String", defaultValue: "" }
            fetchShippingCostInfo: { type: "Boolean", defaultValue: false }
        ) {
            serviceId
            title
            localizedPdpUrl
            classification {
                creationDate
            }
            creators {
                attribution
                creator {
                    displayName
                }
            }
            isArt
            seller @include(if: $showSeller) {
                serviceId
                sellerProfile {
                    company
                }
            }
            linkData {
                ...SeoLink_linkData
            }
            ...Shipping_item
            @arguments(buyerId: $buyerId, fetchShippingCostInfo: $fetchShippingCostInfo)
            ...StorefrontOnly_item
            ...SwatchSelectorMinimal_item
            ...Price_item @arguments(isTrade: $isTrade, pageDisplayEnum: $pageDisplayEnum)
            ...QuickViewItem_item @arguments(showCreatorLinksQuicklook: $showCreatorLinksQuicklook)
            ...ProductTileMeasurements_item
        }
    `,
});
